import { Box, Grid, Typography } from '@mui/material'
import React, { Fragment, useContext, useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { StringsContext, useStrings } from '../../assets/localization/strings'
import { HubspotIndustry, Locale, ProductType, VideoType } from '../../models/GeneralTypes'
import { usePricing } from '../../reusableUtils/pricing'
import ReactHookFormCropperDropzone from '../common/form/ReactHookFormCropperDropzone'
import ReactHookFormRadioCardGroup from '../common/form/ReactHookFormRadioCardGroup'
import ReactHookFormTextField from '../common/form/ReactHookFormTextField'
import type { IUGCCampaign } from './UGCCampaignForm'
import ReactHookFormSelect, { IOption } from '../common/form/ReactHookFormSelect'
import rootStore from '../../stores/rootStore'
import { getCurrencySymbolByCountry } from '../../reusableUtils/currency'
import BrandInfo, { BrandInfoJustLogo } from '../createBrand/BrandInfo'
import ReactHookFormRichTextField from '../common/form/ReactHookFormWithRichText'

const CampaignProduct = () => {
  const strings = useStrings()
  const { pricing } = usePricing()
  const { selectedBrand } = rootStore.brandStore
  const { control, setValue, getValues } = useFormContext<IUGCCampaign>()
  const type = useWatch({ control, name: 'productType' })
  const isAgency = selectedBrand?.isAgency ?? false
  const { language } = useContext(StringsContext)

  useEffect(() => {
    setValue('locale', type == ProductType.digital ? Locale.MONEY : Locale.DELIVERY)
  }, [type])

  if (pricing instanceof Error || pricing == null) return null

  const industryOptions: IOption<string>[] = [
    { value: HubspotIndustry.Fashion_Accessoires, label: strings.brand_creation_info_industry_option_apparel },
    { value: HubspotIndustry.Beauty_Make_Up, label: strings.brand_creation_info_industry_option_beauty },
    { value: HubspotIndustry.Interior_DIY, label: strings.brand_creation_info_industry_option_interior_diy },
    { value: HubspotIndustry.Mommy_Kids, label: strings.brand_creation_info_industry_option_mommy_kids },
    { value: HubspotIndustry.Gesundheit_Fitness, label: strings.brand_creation_info_industry_option_health },
    { value: HubspotIndustry.Technik, label: strings.brand_creation_info_industry_option_technik },
    { value: HubspotIndustry.Buecher_Schreibwaren, label: strings.brand_creation_info_industry_option_books },
    { value: HubspotIndustry.Travel, label: strings.brand_creation_info_industry_option_travel },
    { value: HubspotIndustry.Food, label: strings.brand_creation_info_industry_option_food },
    { value: HubspotIndustry.Luxury, label: strings.brand_creation_info_industry_option_luxury },
    { value: HubspotIndustry.Haustier_Haustierpflege, label: strings.brand_creation_info_industry_option_pets },
    { value: HubspotIndustry.Digital_Products_Apps, label: strings.brand_creation_info_industry_option_digital_products },
    { value: HubspotIndustry.Finance_Insurance, label: strings.brand_creation_info_industry_option_finance },
    { value: HubspotIndustry.Diverse, label: strings.brand_creation_info_industry_option_diverse }
  ].filterNotNull()

  const options = [
    {
      title: strings.campaign_creation_general_producttype_physical_title,
      value: ProductType.physical,
      description: strings.campaign_creation_general_producttype_physical_desc,
      image: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/physisch.jpg'
    },
    {
      title: strings.campaign_creation_general_producttype_digital_title,
      value: ProductType.digital,
      description: strings.campaign_creation_general_producttype_digital_desc,
      image: 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/digital.jpg'
    }
  ]
  const selectedVideoType = getValues('videoType')
  const isUnboxing = selectedVideoType == VideoType.unboxing
  const currencySymbol = rootStore.brandStore.selectedBrand?.brandCountry ? getCurrencySymbolByCountry(rootStore.brandStore.selectedBrand?.brandCountry) : '€'

  return (
    <Grid container>
      {!isUnboxing && (
        <Fragment>
          <Grid item xs={12}>
            <Typography variant='h5' style={{ fontFamily: 'Bricolage Grotesque', fontSize: '18px', fontWeight: 500 }}>
              {strings.campaign_creation_general_product_type}
            </Typography>
          </Grid>
          <Grid item xs={12} mt={2}>
            <ReactHookFormRadioCardGroup name='productType' options={options} md={6} />
          </Grid>
        </Fragment>
      )}
      <Grid item xs={12} mt={5}>
        <Typography variant='h5' style={{ fontFamily: 'Bricolage Grotesque', fontSize: '18px', fontWeight: 500 }}>
          {strings.campaign_creation_general_product_info}
        </Typography>
      </Grid>
      <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
        {/* Left Column: Product Name & Price */}
        <div style={{ width: '90%', marginRight: '34px' }}>
          <ReactHookFormTextField
            name='name'
            label={strings.campaign_creation_general_product_name_label}
            placeholder={strings.campaign_creation_general_product_name_placeholder}
          />
          <div style={{ paddingTop: '20px' }} />
          <ReactHookFormTextField name='link' label={strings.campaign_creation_product_website} placeholder='www.abc.com' />
        </div>
        {/* Left Column: Category (Select) & Website (Link) */}
        <div style={{ width: '90%' }}>
          <ReactHookFormTextField name='price' label={strings.campaign_creation_general_product_price_label(currencySymbol)} type='number' />
          <div style={{ paddingTop: '20px' }} />
          <ReactHookFormSelect name='industry' label={strings.brand_creation_info_industry} options={industryOptions} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'start', justifyContent: 'space-between', width: '100%', marginTop: '18px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', justifyContent: 'space-between', width: '50%' }}>
          <Grid container sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '90%', top: '0px' }}>
            <Typography variant='body2' sx={{ fontWeight: 400, fontSiz: '16px' }}>
              {strings.campaign_creation_image_title}
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 400 }}>
              {strings.campaign_creation_app_overview}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <ReactHookFormCropperDropzone
                name='campaignImage'
                label={strings.campaign_creation_general_campaign_image_label}
                accept='image/jpeg, image/png'
                height='200px'
                width='200px'
                aspectRatio={1}
                isProductImage
              />
            </Box>
          </Grid>
        </div>
        <img
          src={
            language.startsWith('en')
              ? 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/Mockup_UK_1.png'
              : 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/Mockup-2.png'
          }
          alt='phone mock'
          style={{ margin: 'auto', width: '280px' }}
        />
      </div>
      {isAgency || !selectedBrand?.brandLogo ? (
        isAgency ? (
          <div style={{ width: '100%', padding: '24px', marginBottom: '22px', borderStyle: 'solid', borderRadius: '16px', borderColor: '#17181B33', borderWidth: '1px' }}>
            <BrandInfo campaignForm={true} />
          </div>
        ) : (
          <div style={{ width: '100%', padding: '24px', marginBottom: '22px', borderStyle: 'solid', borderRadius: '16px', borderColor: '#17181B33', borderWidth: '1px' }}>
            <BrandInfoJustLogo />
          </div>
        )
      ) : null}
      <Grid item xs={12} mt={2}>
        <ReactHookFormRichTextField
          name='description'
          label={strings.campaign_creation_general_product_desc_label}
          placeholder={strings.campaign_creation_general_product_desc_placeholder}
          minHeight={120}
          maxHeight={300}
          toolbar={true}
        />
      </Grid>
    </Grid>
  )
}

export default CampaignProduct
